/**
 * RitualHistoryCalendar - Visual calendar showing ritual completion history
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Sunrise, Moon, CheckCircle2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfDay, eachDayOfInterval, isSameDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface RitualDay {
  date: Date;
  morning: boolean;
  evening: boolean;
}

export const RitualHistoryCalendar: React.FC<{ className?: string }> = ({ className }) => {
  const { data: ritualDays, isLoading } = useQuery({
    queryKey: ['ritual-history-calendar'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const startDate = format(subDays(new Date(), 27), 'yyyy-MM-dd');
      
      const { data: logs } = await supabase
        .from('daily_ritual_logs')
        .select('log_date, routine_id')
        .eq('user_id', user.id)
        .gte('log_date', startDate)
        .order('log_date', { ascending: true });

      const { data: routines } = await supabase
        .from('user_ritual_routines')
        .select('id, routine_type')
        .eq('user_id', user.id);

      if (!logs || !routines) return [];

      const morningRoutineId = routines.find(r => r.routine_type === 'morning')?.id;
      const eveningRoutineId = routines.find(r => r.routine_type === 'evening')?.id;

      // Create a map of dates to completion status
      const dateMap = new Map<string, { morning: boolean; evening: boolean }>();
      
      logs.forEach(log => {
        const existing = dateMap.get(log.log_date) || { morning: false, evening: false };
        if (log.routine_id === morningRoutineId) existing.morning = true;
        if (log.routine_id === eveningRoutineId) existing.evening = true;
        dateMap.set(log.log_date, existing);
      });

      // Generate last 28 days
      const days = eachDayOfInterval({
        start: subDays(new Date(), 27),
        end: new Date()
      });

      return days.map(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const status = dateMap.get(dateStr) || { morning: false, evening: false };
        return {
          date,
          morning: status.morning,
          evening: status.evening
        };
      });
    },
    staleTime: 5 * 60 * 1000
  });

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  const completedBoth = ritualDays?.filter(d => d.morning && d.evening).length || 0;
  const completedMorning = ritualDays?.filter(d => d.morning).length || 0;
  const completedEvening = ritualDays?.filter(d => d.evening).length || 0;

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Last 28 Days</CardTitle>
          </div>
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="w-3 h-3" />
            {completedBoth} perfect days
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div key={i} className="text-center text-xs text-muted-foreground font-medium py-1">
              {day}
            </div>
          ))}
          
          {ritualDays?.map((day, i) => {
            const isToday = isSameDay(day.date, new Date());
            const bothComplete = day.morning && day.evening;
            const oneComplete = day.morning || day.evening;
            
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.02 }}
                className={cn(
                  "aspect-square rounded-md flex flex-col items-center justify-center gap-0.5 text-xs transition-all",
                  isToday && "ring-2 ring-primary/50",
                  bothComplete 
                    ? "bg-gradient-to-br from-secondary/30 to-primary/30"
                    : oneComplete
                      ? "bg-muted/50"
                      : "bg-muted/20"
                )}
              >
                <span className={cn(
                  "font-medium",
                  bothComplete ? "text-foreground" : "text-muted-foreground"
                )}>
                  {format(day.date, 'd')}
                </span>
                <div className="flex gap-0.5">
                  {day.morning && (
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary" />
                  )}
                  {day.evening && (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-secondary" />
            <span>Morning ({completedMorning})</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span>Evening ({completedEvening})</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
